import { useState, useEffect, useMemo } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Camera, Upload, Loader2, Check, FileImage, FileText, X, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { useServiceLogs } from "@/hooks/useServiceLogs";
import { useAppliances } from "@/hooks/useAppliances";
import { useProviders } from "@/hooks/useProviders";
import { useFileUpload } from "@/hooks/useFileUpload";
import { usePropertyContext } from "@/contexts/PropertyContext";
import { useSubscription } from "@/hooks/useSubscription";
import { Switch } from "@/components/ui/switch";
import AddProviderDialog from "./AddProviderDialog";
import UpgradePlanSheet from "./UpgradePlanSheet";
import { Plus, Lock, Info, AlertTriangle } from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const LogMaintenanceSheet = ({ open, onOpenChange, onSuccess, applianceId: initialApplianceId }) => {
    const { activeProperty } = usePropertyContext();
    const { addServiceLog } = useServiceLogs(activeProperty?.id);
    const { appliances } = useAppliances(activeProperty?.id);
    const { providers, refetch: refetchProviders } = useProviders();
    const { uploading, uploadFile, capturePhoto, selectFile } = useFileUpload();

    const [uploadedFile, setUploadedFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [automatedFollowup, setAutomatedFollowup] = useState(true);
    const [isAddProviderOpen, setIsAddProviderOpen] = useState(false);
    const [showUpgradeSheet, setShowUpgradeSheet] = useState(false);
    const [providerSearch, setProviderSearch] = useState("");
    const { isFeatureLocked, plan } = useSubscription();

    const [formData, setFormData] = useState({
        applianceId: initialApplianceId || "",
        providerId: "",
        date: new Date().toISOString().split("T")[0],
        description: "",
        cost: "",
    });

    // Find the category of the current appliance to prioritize providers
    const applianceCategory = useMemo(() => {
        if (!formData.applianceId || !appliances) return null;
        return appliances.find(a => a.id === formData.applianceId)?.category;
    }, [formData.applianceId, appliances]);

    const [newlyAddedProvider, setNewlyAddedProvider] = useState(null);

    // Filter and Sort Providers
    const filteredProviders = useMemo(() => {
        let list = [...providers];

        // Ensure newly added provider is included in the list for immediate selection
        if (newlyAddedProvider && !list.find(p => p.id === newlyAddedProvider.id)) {
            list.push(newlyAddedProvider);
        }

        if (providerSearch.trim()) {
            const search = providerSearch.toLowerCase();
            // Search all categories when user types
            list = list.filter(p =>
                p.name.toLowerCase().includes(search) ||
                (p.category && p.category.toLowerCase().includes(search))
            );
        } else if (applianceCategory) {
            // Limited to appliance category by default
            list = list.filter(p => p.category === applianceCategory);
        }

        return list.sort((a, b) => a.name.localeCompare(b.name));
    }, [providers, applianceCategory, providerSearch, newlyAddedProvider]);

    const { serviceLogs } = useServiceLogs(activeProperty?.id);

    // Logic 2: Check unique appliances that ALREADY have logs (Max 2)
    const uniqueApplianceIdsWithLogs = new Set(serviceLogs.map(l => l.appliance_id).filter(Boolean));
    // If we are adding to a NEW appliance (one that doesn't have logs yet), does it exceed limit?
    const isNewAppliance = formData.applianceId && !uniqueApplianceIdsWithLogs.has(formData.applianceId);

    const isTotalAppliancesLimitReached = isNewAppliance && isFeatureLocked('maintenance_appliance_limit', uniqueApplianceIdsWithLogs.size);

    const isLimitReached = isTotalAppliancesLimitReached;

    // Update applianceId when prop changes or sheet opens with a pre-selected appliance
    useEffect(() => {
        if (open && initialApplianceId) {
            const timer = setTimeout(() => {
                setFormData(prev => ({ ...prev, applianceId: initialApplianceId }));
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [open, initialApplianceId]);

    const handleCameraCapture = async () => {
        const file = await capturePhoto();
        if (file) {
            const result = await uploadFile(file, "invoices");
            if (result) {
                setUploadedFile(result);
            }
        }
    };

    const handleFileSelect = async () => {
        // Was PDF-only ("application/pdf"), even though the UI copy below promises
        // "photo or upload an image/PDF" — the Upload button couldn't actually pick
        // an image file. AddApplianceSheet's equivalent handler already accepted both.
        const file = await selectFile("application/pdf,image/*");
        if (file) {
            const result = await uploadFile(file, "invoices");
            if (result) {
                setUploadedFile(result);
            }
        }
    };

    const handleRemoveFile = () => {
        setUploadedFile(null);
    };

    const handleSave = async () => {
        setSaving(true);
        const success = await addServiceLog({
            applianceId: formData.applianceId || undefined,
            providerId: formData.providerId || undefined,
            propertyId: activeProperty?.id,
            date: formData.date,
            description: formData.description.trim() || undefined,
            cost: formData.cost ? parseFloat(formData.cost) : undefined,
            invoiceUrl: uploadedFile?.url,
            automatedFollowup,
        });

        setSaving(false);

        if (success) {
            resetForm();
            onOpenChange(false);
            onSuccess?.();
        }
    };

    const resetForm = () => {
        setUploadedFile(null);
        setFormData({
            applianceId: initialApplianceId || "",
            providerId: "",
            date: new Date().toISOString().split("T")[0],
            description: "",
            cost: "",
        });
        setAutomatedFollowup(true);
    };

    const handleClose = (open) => {
        if (!open) resetForm();
        onOpenChange(open);
    };

    const handleFocus = (e) => {
        setTimeout(() => {
            e.target.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 350);
    };

    const isImage = uploadedFile?.type.startsWith("image/");

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    return (
        <Sheet open={open} onOpenChange={handleClose}>
            <SheetContent
                side="bottom"
                onOpenAutoFocus={(e) => e.preventDefault()}
                className="max-h-[85vh] rounded-t-3xl flex flex-col p-0"
                style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}
            >
                <SheetHeader className="pb-4 flex-shrink-0 px-6 pt-8">
                    <SheetTitle className="text-xl font-bold text-foreground">
                        Log Service
                    </SheetTitle>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 space-y-4 pb-4 px-6">
                    {/* Upload Area */}
                    <div className="space-y-3">
                        <Label className="text-sm font-medium text-foreground">
                            Receipt or Invoice (Optional)
                        </Label>

                        {!uploadedFile ? (
                            <div
                                className={cn(
                                    "w-full border-2 border-dashed border-border rounded-xl",
                                    "flex flex-col items-center justify-center gap-3 py-6",
                                    "transition-colors",
                                    uploading ? "opacity-50 pointer-events-none bg-muted/20" : "hover:border-primary/50 hover:bg-accent/50"
                                )}
                            >
                                {uploading ? (
                                    <div className="flex flex-col items-center gap-2">
                                        <Loader2 className="w-6 h-6 text-primary animate-spin" />
                                        <span className="text-sm text-muted-foreground">Uploading to storage...</span>
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex gap-4">
                                            <button
                                                type="button"
                                                onClick={handleCameraCapture}
                                                className="p-3 rounded-full bg-primary/10 hover:bg-primary/20 transition-all active-scale"
                                                aria-label="Take photo"
                                            >
                                                <Camera className="w-6 h-6 text-primary" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleFileSelect}
                                                className="p-3 rounded-full bg-primary/10 hover:bg-primary/20 transition-all active-scale"
                                                aria-label="Upload file"
                                            >
                                                <Upload className="w-6 h-6 text-primary" />
                                            </button>
                                        </div>
                                        <span className="text-sm text-muted-foreground text-center px-4">
                                            Take a photo or upload an image/PDF
                                        </span>
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className="relative w-full p-4 bg-muted/30 rounded-xl border border-border">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                                        {isImage ? (
                                            <FileImage className="w-5 h-5 text-primary" />
                                        ) : (
                                            <FileText className="w-5 h-5 text-primary" />
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-foreground truncate">
                                            {uploadedFile.name}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            {formatFileSize(uploadedFile.size)} • Uploaded
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <div className="p-1 bg-emerald-500 rounded-full">
                                            <Check className="w-3 h-3 text-primary-foreground" />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleRemoveFile}
                                            className="p-1 hover:bg-muted rounded-full transition-colors"
                                            aria-label="Remove file"
                                        >
                                            <X className="w-4 h-4 text-muted-foreground" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Form Fields */}
                    <div className="space-y-4">
                        {/* Appliance Selection */}
                        {appliances.length > 0 && (
                            <div className="space-y-2">
                                <Label>Appliance</Label>
                                <Select
                                    value={formData.applianceId}
                                    onValueChange={(value) => setFormData(prev => ({ ...prev, applianceId: value }))}
                                >
                                    <SelectTrigger className="rounded-xl ring-1 ring-primary/20">
                                        <SelectValue placeholder="Select appliance (Required)" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {appliances.map((appliance) => (
                                            <SelectItem key={appliance.id} value={appliance.id}>
                                                {appliance.brand} {appliance.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {/* Provider Selection */}
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label>Service Provider</Label>
                                {applianceCategory && (
                                    <span className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
                                        Prioritizing {applianceCategory}
                                    </span>
                                )}
                            </div>

                            <Select
                                value={formData.providerId}
                                onValueChange={(value) => {
                                    if (value === "add-new") {
                                        setIsAddProviderOpen(true);
                                    } else {
                                        setFormData(prev => ({ ...prev, providerId: value }));
                                    }
                                }}
                            >
                                <SelectTrigger className="rounded-xl bg-white/50">
                                    <SelectValue placeholder="Select provider (optional)" />
                                </SelectTrigger>
                                <SelectContent className="bg-popover border border-border z-[100] max-h-[300px]">
                                    <div className="px-2 py-2 sticky top-0 bg-popover z-10 border-b mb-1">
                                        <div className="relative">
                                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                                            <Input
                                                placeholder="Search all providers..."
                                                value={providerSearch}
                                                onChange={(e) => setProviderSearch(e.target.value)}
                                                className="h-8 pl-8 text-xs rounded-lg"
                                                onClick={(e) => e.stopPropagation()}
                                                onPointerDown={(e) => e.stopPropagation()}
                                                onKeyDown={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                    </div>

                                    <div className="overflow-y-auto">
                                        {filteredProviders.length === 0 ? (
                                            <div className="py-6 text-center text-xs text-muted-foreground">
                                                No providers found matching "{providerSearch}"
                                            </div>
                                        ) : (
                                            <>
                                                {/* Matching Category Group */}
                                                {filteredProviders.some(p => p.category === applianceCategory) && !providerSearch && (
                                                    <div className="px-2 py-1.5 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                                                        Recommended for {applianceCategory}
                                                    </div>
                                                )}

                                                {filteredProviders.map((provider) => (
                                                    <SelectItem key={provider.id} value={provider.id}>
                                                        <div className="flex items-center justify-between w-full gap-2">
                                                            <span>{provider.name}</span>
                                                            <span className="text-[10px] opacity-50 italic">
                                                                {provider.category}
                                                            </span>
                                                        </div>
                                                    </SelectItem>
                                                ))}
                                            </>
                                        )}

                                        <div className="border-t mt-1 pt-1">
                                            <SelectItem value="add-new" className="text-primary font-bold">
                                                <div className="flex items-center gap-2">
                                                    <Plus className="w-4 h-4 ml-1 mr-2" />
                                                    Add New Provider
                                                </div>
                                            </SelectItem>
                                        </div>
                                    </div>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="description">Work Description</Label>
                            <Textarea
                                id="description"
                                value={formData.description}
                                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                                onFocus={handleFocus}
                                placeholder="Brief description of work done"
                                className="rounded-xl resize-none"
                                rows={3}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="date">Date</Label>
                            <Input
                                id="date"
                                type="date"
                                value={formData.date}
                                onChange={(e) => setFormData((prev) => ({ ...prev, date: e.target.value }))}
                                onFocus={handleFocus}
                                className="rounded-xl w-full"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="cost">Cost (R)</Label>
                            <Input
                                id="cost"
                                type="number"
                                inputMode="decimal"
                                value={formData.cost}
                                onChange={(e) => setFormData((prev) => ({ ...prev, cost: e.target.value }))}
                                onFocus={handleFocus}
                                placeholder="0.00"
                                className="rounded-xl"
                            />
                        </div>
                    </div>

                    {/* Automated Follow-up Toggle */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between p-4 bg-muted/30 rounded-xl border border-border">
                            <div className="flex-1 pr-4">
                                <p className="text-sm font-medium text-foreground">Notify Provider</p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    Automatically email this provider when the next service is due
                                </p>
                            </div>
                            <Switch
                                checked={automatedFollowup}
                                onCheckedChange={(checked) => setAutomatedFollowup(checked)}
                            />
                        </div>

                        {/* Provider Email Warning */}
                        {automatedFollowup && formData.providerId && (() => {
                            const selectedProvider = providers.find(p => p.id === formData.providerId);
                            const hasEmail = selectedProvider && (selectedProvider.email || selectedProvider.email_2);
                            if (selectedProvider && !hasEmail) {
                                return (
                                    <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                                        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                                        <div className="space-y-1">
                                            <p className="text-sm font-bold text-amber-900">Provider has no email</p>
                                            <p className="text-xs text-amber-700 leading-relaxed">
                                                You have enabled provider notifications, but this provider does not have an email address on file. We won't be able to notify them automatically.
                                            </p>
                                        </div>
                                    </div>
                                );
                            }
                            return null;
                        })()}
                    </div>
                </div>

                <div className="flex-shrink-0 pt-2 pb-2 px-6">
                    {isLimitReached ? (
                        <div className="space-y-3">
                            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                                <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                                <div className="space-y-1">
                                    <p className="text-sm font-bold text-amber-900">Service Log Limit Reached</p>
                                    <p className="text-xs text-amber-700 leading-relaxed">
                                        Free tier allows logging service history on max 2 appliances. Upgrade to cover your full home portfolio.
                                    </p>
                                </div>
                            </div>
                            <Button
                                onClick={() => setShowUpgradeSheet(true)}
                                className="w-full rounded-xl py-6 bg-amber-500 hover:bg-amber-600 text-white font-bold gap-2 active-scale transition-all"
                            >
                                <Lock className="w-4 h-4" />
                                Upgrade to Log More
                            </Button>
                        </div>
                    ) : (
                        <Button
                            onClick={handleSave}
                            className="w-full rounded-xl py-6 active-scale transition-all shadow-lg"
                            disabled={saving || !formData.applianceId}
                        >
                            {!formData.applianceId ? (
                                "Select an appliance to save"
                            ) : saving ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    Saving...
                                </>
                            ) : (
                                "Save Service Log"
                            )}
                        </Button>
                    )}
                </div>

                <AddProviderDialog
                    open={isAddProviderOpen}
                    onOpenChange={setIsAddProviderOpen}
                    onSuccess={(newProvider) => {
                        setNewlyAddedProvider(newProvider);
                        setFormData(prev => ({ ...prev, providerId: newProvider.id }));
                        refetchProviders();
                    }}
                />

                <UpgradePlanSheet
                    open={showUpgradeSheet}
                    onOpenChange={setShowUpgradeSheet}
                    currentPlan={plan}
                />
            </SheetContent>
        </Sheet>
    );
};

export default LogMaintenanceSheet;
